import type { BentoCardButton } from "@/components/BentoBox/types";
import getTextFromComponent from "@/lib/get-text-from-component";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import { resolveXfinityClickOffUrl } from "@/lib/clickthrough-destination";
import type { TAGULAR_ELEMENT } from "@/types/tagular";
import React from "react";

interface ZoomWrapperProps {
  enableContentZoom: boolean;
  bottomCta?: BentoCardButton;
  elementName: string;
  title: string | React.ReactNode;
  tagularProps?: TAGULAR_ELEMENT;
  cardId: string;
  children: React.ReactNode;
}

const ZoomWrapper: React.FC<ZoomWrapperProps> = ({
  enableContentZoom,
  bottomCta,
  elementName,
  title,
  tagularProps,
  cardId,
  children,
}) => {
  if (!enableContentZoom) return <>{children}</>;

  const cardText = getTextFromComponent(title ?? "");
  const resolvedBottomCtaHref = resolveXfinityClickOffUrl(bottomCta?.href);

  const handleCardClick = () => {
    if (!resolvedBottomCtaHref) return;

    fireElementClicked({
      elementType: "card",
      text: cardText,
      location: "bento",
      name: `cardBento${elementName}`,
      outboundUrl: resolvedBottomCtaHref,
      ...bottomCta?.tagularProps,
      ...tagularProps,
    });
  };

  return (
    <div className={`bento-card-inner-${cardId} relative no-underline h-full`}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .bento-card-inner-${cardId} a,
            .bento-card-inner-${cardId} button,
            .bento-card-inner-${cardId} input,
            .bento-card-inner-${cardId} select,
            .bento-card-inner-${cardId} textarea,
            .bento-card-inner-${cardId} summary,
            .bento-card-inner-${cardId} [role="button"],
            .bento-card-inner-${cardId} [role="link"],
            .bento-card-inner-${cardId} [tabindex]:not([tabindex="-1"]) {
              pointer-events: auto;
            }
          `,
        }}
      />
      {resolvedBottomCtaHref ? (
        <a
          aria-label={cardText}
          className="absolute inset-0 z-0 rounded-2xl focus-visible:z-20 focus-visible:outline-2 focus-visible:-outline-offset-4"
          href={resolvedBottomCtaHref}
          onClick={handleCardClick}
        />
      ) : null}
      <div className="contents z-10 pointer-events-none">{children}</div>
    </div>
  );
};

export default ZoomWrapper;
